import React, { useCallback, useEffect, useRef } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LOADER_OPTIONS } from '../config/googleMaps';

const MAP_CONTAINER_STYLE = { width: '100%', height: '280px', borderRadius: '1rem' };
const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 }; // India, used until a location is picked

function parseGeocodeResult(result) {
  const comps = result.address_components || [];
  const get = (...types) => {
    const found = comps.find((c) => types.some((t) => c.types.includes(t)));
    return found ? found.long_name : '';
  };

  const streetNumber = get('street_number');
  const route = get('route');
  const sublocality = get('sublocality_level_1', 'sublocality_level_2', 'sublocality', 'neighborhood');
  const locality = get('locality');
  const state = get('administrative_area_level_1');
  const pincode = get('postal_code');
  const premise = get('premise', 'establishment', 'point_of_interest', 'landmark');
  const ward = get('administrative_area_level_3', 'administrative_area_level_2');

  // Build line1 from most specific to least specific
  let line1 = [streetNumber, route].filter(Boolean).join(', ');
  if (!line1) line1 = premise;
  if (!line1) line1 = ward;
  if (!line1) line1 = result.formatted_address?.split(',')[0] || '';

  // line2 should not duplicate line1
  const line2 = sublocality && sublocality !== line1 ? sublocality : '';

  return {
    line1,
    line2,
    city: locality,
    state,
    pincode,
    landmark: '',
  };
}

// Lets the customer drag/click a pin on a map to set the exact delivery
// coordinates, instead of relying only on the browser's current-location fix.
const LocationPickerMap = ({ coordinates, onChange, onAddressChange }) => {
  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);
  const geocoderRef = useRef(null);
  const geocodeRequestRef = useRef(0);

  const reverseGeocode = useCallback(
    (coords) => {
      if (!window.google?.maps || !onAddressChange) return;
      const requestId = ++geocodeRequestRef.current;
      if (!geocoderRef.current) {
        geocoderRef.current = new window.google.maps.Geocoder();
      }
      geocoderRef.current.geocode({ location: coords }, (results, status) => {
        if (requestId !== geocodeRequestRef.current) return;
        if (status === 'OK' && results && results[0]) {
          onAddressChange(parseGeocodeResult(results[0]));
        }
      });
    },
    [onAddressChange]
  );

  const handleClick = useCallback(
    (event) => {
      onChange({ lat: event.latLng.lat(), lng: event.latLng.lng() });
    },
    [onChange]
  );

  const handleMarkerDragEnd = useCallback(
    (event) => {
      onChange({ lat: event.latLng.lat(), lng: event.latLng.lng() });
    },
    [onChange]
  );

  // Reverse-geocode coordinates that come from outside (e.g. "Use my current location")
  useEffect(() => {
    if (isLoaded && coordinates) {
      reverseGeocode(coordinates);
    } else if (!coordinates) {
      geocodeRequestRef.current += 1;
    }
  }, [coordinates, isLoaded, reverseGeocode]);

  useEffect(() => () => {
    geocodeRequestRef.current += 1;
  }, []);

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4 text-sm text-emerald-800">
        Map picker is unavailable — Google Maps API key is not configured.
      </div>
    );
  }

  if (!isLoaded) {
    return <div className="rounded-2xl bg-emerald-50 h-[280px] animate-pulse" aria-hidden="true" />;
  }

  return (
    <div>
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={coordinates || DEFAULT_CENTER}
        zoom={coordinates ? 16 : 5}
        onClick={handleClick}
        options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
      >
        {coordinates && (
          <Marker position={coordinates} draggable onDragEnd={handleMarkerDragEnd} />
        )}
      </GoogleMap>
      <p className="text-xs text-ink-secondary mt-2">
        Click anywhere on the map, or drag the pin, to set the exact delivery location.
      </p>
    </div>
  );
};

export default LocationPickerMap;
